import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollReveal from '@/components/base/ScrollReveal';
import JsonLd from '@/components/base/JsonLd';

const categories = [
  { id: 'getting-started', icon: 'ri-rocket-line', labelKey: 'helpCenter.gettingStarted' as const },
  { id: 'membership', icon: 'ri-user-add-line', labelKey: 'helpCenter.membership' as const },
  { id: 'billing', icon: 'ri-money-dollar-circle-line', labelKey: 'helpCenter.billing' as const },
  { id: 'technical', icon: 'ri-settings-line', labelKey: 'helpCenter.technical' as const },
  { id: 'mobile-app', icon: 'ri-smartphone-line', labelKey: 'helpCenter.mobileApp' as const },
  { id: 'reports', icon: 'ri-bar-chart-line', labelKey: 'helpCenter.reports' as const },
];

const faqData: { category: string; questionKey: string; answerKey: string }[] = [
  { category: 'getting-started', questionKey: 'helpCenter.faq1.question', answerKey: 'helpCenter.faq1.answer' },
  { category: 'getting-started', questionKey: 'helpCenter.faq2.question', answerKey: 'helpCenter.faq2.answer' },
  { category: 'membership', questionKey: 'helpCenter.faq3.question', answerKey: 'helpCenter.faq3.answer' },
  { category: 'membership', questionKey: 'helpCenter.faq4.question', answerKey: 'helpCenter.faq4.answer' },
  { category: 'billing', questionKey: 'helpCenter.faq5.question', answerKey: 'helpCenter.faq5.answer' },
  { category: 'billing', questionKey: 'helpCenter.faq6.question', answerKey: 'helpCenter.faq6.answer' },
  { category: 'technical', questionKey: 'helpCenter.faq7.question', answerKey: 'helpCenter.faq7.answer' },
  { category: 'technical', questionKey: 'helpCenter.faq8.question', answerKey: 'helpCenter.faq8.answer' },
  { category: 'mobile-app', questionKey: 'helpCenter.faq9.question', answerKey: 'helpCenter.faq9.answer' },
  { category: 'mobile-app', questionKey: 'helpCenter.faq10.question', answerKey: 'helpCenter.faq10.answer' },
  { category: 'reports', questionKey: 'helpCenter.faq11.question', answerKey: 'helpCenter.faq11.answer' },
  { category: 'reports', questionKey: 'helpCenter.faq12.question', answerKey: 'helpCenter.faq12.answer' },
];

const heroImg = 'https://readdy.ai/api/search-image?query=Abstract%20geometric%20minimalist%20background%20warm%20coral%20terracotta%20soft%20gradients%20clean%20modern%20design%20subtle%20flowing%20shapes%20professional%20atmosphere%20editorial%20quality%20high%20detail%20no%20text%20no%20people&width=1800&height=600&seq=help-center-hero&orientation=landscape';

const siteUrl = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL) || 'https://5vix.com';

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '5vix',
  url: siteUrl,
  description: 'Integrated gym management system for membership, scheduling, billing, attendance, and analytics.',
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+201044553254',
    contactType: 'customer support',
    availableLanguage: ['English', 'Arabic'],
  },
  sameAs: [
    'https://www.instagram.com/5vixofficial/',
    'https://www.tiktok.com/@5vixofficial',
    'https://www.youtube.com/@5vix-c3p',
    'https://www.linkedin.com/in/5vix-software-12058b363/',
  ],
};

export default function HelpCenterPage() {
  const { t, i18n } = useTranslation();
  const [activeCategory, setActiveCategory] = useState('getting-started');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [isRTL, i18n.language]);

  const pageJsonLd = [
    organizationSchema,
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqData.map((faq) => ({
        '@type': 'Question',
        name: t(faq.questionKey),
        acceptedAnswer: {
          '@type': 'Answer',
          text: t(faq.answerKey),
        },
      })),
    },
  ];

  const filteredFaqs = faqData
    .map((faq, i) => ({ ...faq, originalIndex: i }))
    .filter((faq) => {
      if (faq.category !== activeCategory) return false;
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return (
        t(faq.questionKey).toLowerCase().includes(q) ||
        t(faq.answerKey).toLowerCase().includes(q)
      );
    });

  const toggleFaq = (index: number) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  return (
    <div className="bg-background-50 min-h-screen">
      <JsonLd data={pageJsonLd} />
      <Navbar />

      {/* Hero Section */}
      <section className="relative w-full min-h-[380px] md:min-h-[440px] flex items-center overflow-hidden bg-gradient-to-br from-primary-950 via-primary-900 to-primary-800">
        <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-primary-400/20 via-transparent to-transparent"></div>
        <div className="relative z-10 w-full px-4 md:px-6 py-16 md:py-20">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-background-50 mb-4">
              {t('helpCenter.title')}
            </h1>
            <p className="text-background-50/80 text-base md:text-lg mb-8">
              {t('helpCenter.subtitle')}
            </p>
            <div className="relative max-w-2xl mx-auto">
              <div className="absolute inset-y-0 left-0 rtl:left-auto rtl:right-0 flex items-center pl-4 rtl:pl-0 rtl:pr-4 pointer-events-none z-10">
                <i className="ri-search-line text-foreground-400 text-lg"></i>
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('helpCenter.searchPlaceholder')}
                className="w-full pl-11 rtl:pl-4 rtl:pr-11 pr-4 py-3.5 text-sm bg-background-50 rounded-lg border border-background-200 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent text-foreground-950 placeholder:text-foreground-400"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Category Tabs */}
      <ScrollReveal>
      <section className="py-10 md:py-14 bg-background-50">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 md:gap-4">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => {
                  setActiveCategory(cat.id);
                  setExpandedFaq(null);
                }}
                className={`flex flex-col items-center p-5 rounded-xl border-2 transition-all cursor-pointer ${
                  activeCategory === cat.id
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-background-200 bg-background-50 hover:border-primary-300'
                }`}
              >
                <div
                  className={`w-11 h-11 rounded-full flex items-center justify-center mb-3 transition-all ${
                    activeCategory === cat.id
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-primary-500'
                  }`}
                >
                  <i className={`${cat.icon} text-lg`}></i>
                </div>
                <span
                  className={`text-xs font-medium text-center leading-tight transition-colors ${
                    activeCategory === cat.id ? 'text-primary-600' : 'text-foreground-700'
                  }`}
                >
                  {t(cat.labelKey)}
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>
      </ScrollReveal>

      {/* FAQ Section */}
      <ScrollReveal>
      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-10 text-center">
            {t('helpCenter.frequentlyAsked')}
          </h2>

          {filteredFaqs.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-background-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-search-line text-foreground-400 text-2xl"></i>
              </div>
              <p className="text-foreground-500 text-base">{t('helpCenter.noResults')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredFaqs.map((faq) => (
                <div
                  key={faq.originalIndex}
                  className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden transition-all"
                >
                  <button
                    onClick={() => toggleFaq(faq.originalIndex)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left cursor-pointer hover:bg-background-100 transition-colors"
                  >
                    <span className="font-heading font-semibold text-foreground-950 text-sm pr-4 rtl:pr-0 rtl:pl-4 leading-snug">
                      {t(faq.questionKey)}
                    </span>
                    <div className="w-6 h-6 flex items-center justify-center shrink-0 text-primary-500">
                      <i
                        className={`${
                          expandedFaq === faq.originalIndex
                            ? 'ri-subtract-line'
                            : 'ri-add-line'
                        } text-lg transition-transform`}
                      ></i>
                    </div>
                  </button>
                  {expandedFaq === faq.originalIndex && (
                    <div className="px-6 pb-5 text-foreground-600 text-sm leading-relaxed border-t border-background-200/60 pt-4">
                      {t(faq.answerKey)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
      </ScrollReveal>

      {/* Contact Support */}
      <ScrollReveal>
      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="bg-primary-500 rounded-2xl p-8 md:p-12 text-center">
            <div className="w-16 h-16 bg-background-50/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-customer-service-2-line text-3xl text-background-50"></i>
            </div>
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-4">
              {t('helpCenter.stillNeedHelp')}
            </h2>
            <p className="text-background-50/85 text-base mb-8 max-w-lg mx-auto leading-relaxed">
              {t('helpCenter.contactTeam')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://wa.me/+201044553254"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="bg-background-50 text-primary-600 px-7 py-3 rounded-lg hover:bg-background-100 transition-all whitespace-nowrap cursor-pointer font-semibold text-sm inline-flex items-center justify-center gap-2"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-whatsapp-line"></i>
                </div>
                {t('helpCenter.whatsappSupport')}
              </a>
              <a
                href="tel:201044553254"
                className="bg-background-50/10 text-background-50 border-2 border-background-50/40 px-7 py-3 rounded-lg hover:bg-background-50/20 transition-all whitespace-nowrap cursor-pointer font-semibold text-sm inline-flex items-center justify-center gap-2"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-line"></i>
                </div>
                {t('helpCenter.callUs')}
              </a>
            </div>
          </div>
        </div>
      </section>
      </ScrollReveal>

      {/* CTA */}
      <ScrollReveal>
      <section className="py-14 md:py-20 bg-primary-500">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-2xl md:text-4xl font-heading font-bold text-background-50 mb-5">
            {t('cta.title')}
          </h2>
          <p className="text-background-50/85 text-base md:text-lg mb-8 leading-relaxed">
            {t('cta.description')}
          </p>
          <Link
            to="/#register"
            className="inline-block bg-background-50 text-primary-600 px-10 py-3.5 rounded-lg hover:bg-background-100 transition-all whitespace-nowrap cursor-pointer font-semibold text-sm"
          >
            {t('cta.button')}
          </Link>
        </div>
      </section>
      </ScrollReveal>

      <Footer />
    </div>
  );
}